import { Artist } from "./library-objects";
import { Instrument } from "./music-objects";
import { Project, ProjectSubmission } from "./project-objects";
import { Utils } from "./utils";

export const allowedRoles = ["ConductionAdmin", "ClassroomAdmin", "Student", "Guest", "User", "Transcriber", "Instrumentalist", "Artist", "InstrumentalistLiaison"] as const;
export type Role = (typeof allowedRoles)[number];

export class User {
	public readonly id: string;
	public readonly firstName: string | null;
	public readonly lastName: string | null;
	public readonly username: string;
	public readonly email: string | null;
	public readonly emailConfirmed: boolean;
	public readonly dateCreated: Date;
	public readonly roles: Role[];
	public readonly classroomId: string | undefined;
	public readonly classroom: Classroom | undefined;
	public readonly projectIds: string[];
	public readonly tokens: ProjectSubmissionToken[] | undefined;
	public readonly completedBeginnerTutorial: boolean;
	public readonly artists: Artist[];
	public readonly instruments: Instrument[];
	public readonly isVideoEditor: boolean;
	public readonly isActiveInstrumentalist: boolean;
	public readonly instrumentalistClassroomIds: string[]; 
	public readonly agreedToCurrentTos: boolean | undefined;
	public readonly location: Location | undefined;
	public readonly uploadedProfileImage: boolean;

	private constructor(
		id: string,
		firstName: string | null,
		lastName: string | null,
		username: string,
		email: string | undefined,
		emailConfirmed: boolean,
		dateCreated: Date,
		roles: Role[],
		classroomId: string | undefined,
		classroom: Classroom | undefined,
		projectIds: string[],
		tokens: ProjectSubmissionToken[] | undefined,
		completedBeginnerTutorial: boolean,
		artists: Artist[],
		instruments: Instrument[],
		isVideoEditor: boolean,
		isActiveInstrumentalist: boolean,
		instrumentalistClassroomIds: string[],
		agreedToCurrentTos?: boolean,
		location?: Location,
		uploadedProfileImage: boolean = false
	) {
		this.id = id;
		this.firstName = firstName;
		this.lastName = lastName;
		this.username = username;
		this.email = email || null;
		this.emailConfirmed = emailConfirmed;
		this.dateCreated = dateCreated;
		this.roles = roles;
		this.classroomId = classroomId;
		this.classroom = classroom;
		this.projectIds = projectIds;
		this.tokens = tokens;
		this.completedBeginnerTutorial = completedBeginnerTutorial;
		this.artists = artists;
		this.instruments = instruments;
		this.isVideoEditor = isVideoEditor;
		this.isActiveInstrumentalist = isActiveInstrumentalist;
		this.instrumentalistClassroomIds = instrumentalistClassroomIds;
		this.agreedToCurrentTos = agreedToCurrentTos;
		this.location = location;
		this.uploadedProfileImage = uploadedProfileImage;
	}

	public static fromJson(json: any): User {
		return new User(
			json.userId,
			json.firstName ?? null,
			json.lastName ?? null,
			json.userName,
			json.email,
			json.emailConfirmed,
			Utils.assumeUtcDate(json.dateCreated),
			json.roles?.filter((role: string) => allowedRoles.includes(role as Role)) ?? [],
			json.classroomId,
			json.classroom ? Classroom.fromJson(json.classroom) : undefined,
			json.projectIds,
			json.tokens?.map((token: any) => ProjectSubmissionToken.fromJson(token)),
			json.completedBeginnerTutorial,
			json.artists.map((artist: any) => Artist.fromJson(artist)),
			json.instruments.map((instrument: any) => Instrument.fromJson(instrument)),
			json.isVideoEditor,
			json.isActiveInstrumentalist,
			json.instrumentalistClassroomIds,
			json.agreedToCurrentTos,
			json.location ? Location.fromJson(json.location) : undefined,
			json.uploadedProfileImage
		);
	}

	public static getGuestUser() {
		return new User(
			"guest",
			null,
			null,
			"Guest",
			undefined,
			false,
			new Date(),
			["Guest"],
			undefined,
			undefined,
			[],
			undefined,
			false,
			[],
			[],
			false,
			false,
			[],
			undefined,
			undefined,
			false
		);
	}
}

export class Classroom {
	private classroomAdmins: User[] | undefined;
	private students: User[] | undefined;
	private projects: Project[] | undefined;

	public readonly id: string;
	public readonly name: string;
	public readonly desiredName: string;
	public readonly dateCreated: Date;
	public readonly classroomAdminLimit: number;
	public readonly desiredClassroomAdminLimit: number;
	public readonly studentLimit: number;
	public readonly desiredStudentLimit: number;
	public readonly projectCount: number;
	public readonly submissionCount: number;
	public readonly tokens: ProjectSubmissionToken[] | undefined;
	public readonly defaultSongIds: string[];
	public readonly classroomAdminAccessCode: string | undefined;
	public readonly studentAccessCode: string | undefined;
	public readonly instrumentalistIds: string[] | undefined;

	private constructor(
		id: string,
		name: string,
		dateCreated: Date,
		classroomAdminLimit: number,
		studentLimit: number,
		projectCount: number,
		submissionCount: number,
		tokens: ProjectSubmissionToken[] | undefined,
		defaultSongIds: string[],
		instrumentalistIds: string[] | undefined,
		classroomAdminAccessCode?: string,
		studentAccessCode?: string,
		desiredClassroomAdminLimit?: number,
		desiredStudentLimit?: number,
		desiredName?: string
	) {
		this.id = id;
		this.name = name;
		this.desiredName = desiredName ?? name;
		this.dateCreated = dateCreated;
		this.classroomAdminLimit = classroomAdminLimit;
		this.studentLimit = studentLimit;
		this.desiredClassroomAdminLimit = desiredClassroomAdminLimit ?? classroomAdminLimit;
		this.desiredStudentLimit = desiredStudentLimit ?? studentLimit;
		this.projectCount = projectCount;
		this.submissionCount = submissionCount;
		this.tokens = tokens;
		this.defaultSongIds = defaultSongIds;
		this.classroomAdminAccessCode = classroomAdminAccessCode;
		this.studentAccessCode = studentAccessCode;
		this.instrumentalistIds = instrumentalistIds;
	}

	public setDesiredName(desiredName: string): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.instrumentalistIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			this.desiredStudentLimit,
			desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredClassroomAdminLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.instrumentalistIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			desiredLimit,
			this.desiredStudentLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredStudentLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.instrumentalistIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			desiredLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public loadProjects(projects: Project[]): void {
		this.projects = projects;
	}

	public getProjects(): Project[] | undefined {
		return this.projects;
	}

	public loadClassroomAdmins(classroomAdmins: User[]): void {
		this.classroomAdmins = classroomAdmins;
	}

	public getClassroomAdmins(): User[] | undefined {
		return this.classroomAdmins;
	}

	public loadClassroomStudents(classroomStudents: User[]): void {
		this.students = classroomStudents;
	}

	public getClassroomStudents(): User[] | undefined {
		return this.students;
	}

	public getRemainingActiveCredits(): number | undefined {
		return this.tokens?.filter((currentToken) => currentToken.isActive()).reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getRemainingActiveCredits(), 0) ?? undefined;
	}

	public createCopy(): Classroom {
		return new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.instrumentalistIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			this.desiredStudentLimit,
			this.desiredName
		);
	}

	public static fromJson(json: any): Classroom {
		return new Classroom(
			json.id,
			json.name,
			Utils.assumeUtcDate(json.dateCreated),
			json.classroomAdminLimit,
			json.studentLimit,
			json.projectCount,
			json.submissionCount,
			json.tokens.map((token: any) => ProjectSubmissionToken.fromJson(token)),
			json.defaultSongIds,
			json.instrumentalistIds,
			json.classroomAdminAccessCode,
			json.studentAccessCode
		);
	}
}

export class ProjectSubmissionToken {
	public readonly id: string;
	public readonly credits: number;
	public readonly dateCreated: Date;
	public readonly startDate: Date;
	public readonly expirationDate: Date;
	public readonly submissions: ProjectSubmission[];

	private constructor(
		id: string,
		credits: number,
		dateCreated: Date,
		startDate: Date,
		expirationDate: Date,
		submissions: ProjectSubmission[]
	) {
		this.id = id;
		this.credits = credits;
		this.dateCreated = dateCreated;
		this.startDate = startDate;
		this.expirationDate = expirationDate;
		this.submissions = submissions;
	}

	public getRemainingActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits - this.submissions.length;
	}

	public getActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits;
	}

	public passedStartDate() {
		// Get the current UTC date and time
		const nowUtc = new Date();
		return nowUtc >= this.startDate;
	}

	public isExpired() {
		// Get the current UTC date and time
		const nowUtc = new Date();
		return nowUtc > this.expirationDate;
	}

	public isActive() {
		return this.passedStartDate() && !this.isExpired();
	}

	public static fromJson(json: any): ProjectSubmissionToken {
		return new ProjectSubmissionToken(
			json.id,
			json.credits,
			Utils.assumeUtcDate(json.created),
			Utils.assumeUtcDate(json.start ?? 0),
			Utils.assumeUtcDate(json.expiration),
			json.submissions
		);
	}
}

export class Location {
	public readonly id: string;
	public readonly displayName: string;
	public readonly city: string;
	public readonly region: string | null;
	public readonly countryCode: string;
	public readonly postalCode: string | null;
	public readonly primaryColor: string | null;
	public readonly secondaryColor: string | null;
	public readonly latitude: number;
	public readonly longitude: number;

	private constructor(
		id: string,
		displayName: string,
		city: string,
		region: string | null,
		countryCode: string,
		postalCode: string | null,
		primaryColor: string | null,
		secondaryColor: string | null,
		latitude: number,
		longitude: number
	) {
		this.id = id;
		this.displayName = displayName;
		this.city = city;
		this.region = region;
		this.countryCode = countryCode;
		this.postalCode = postalCode;
		this.primaryColor = primaryColor;
		this.secondaryColor = secondaryColor;
		this.latitude = latitude;
		this.longitude = longitude;
	}

	public static fromJson(json: any): Location {
		return new Location(
			json.locationId,
			json.displayName,
			json.city,
			json.region ?? null,
			json.countryCode,
			json.postalCode ?? null,
			json.primaryColor ?? null,
			json.secondaryColor ?? null,
			json.latitude,
			json.longitude
		);
	}
}